<template>
  <div>
    <div class="export-div">
      <button
        class="btn btn-outline-primary"
        @click="
          csvExportClientProfitabilityDashboard(clientsProfitabilityDashboard)
        "
      >
        Download Table
      </button>
      <button class="btn btn-outline-primary ms-5" @click="clearFilterList()">
        Clear Filter
      </button>
      <h6 style="color: red" class="ms-5 ps-5 mt-1 pt-2">
        If "Months Into The Contract" is below 1 month we do not compute
      </h6>
    </div>
    <div
      v-if="!loading"
      class="d-flex justify-content-center align-items-center m-5"
    >
      <div
        class="spinner-border text-dark"
        style="width: 7.5rem; height: 7.5rem"
        role="status"
      >
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <div v-else>
      <v-card flat class="table-wrapper d-flex">
        <v-col cols="12">
          <v-row>
            <v-col cols="12">
              <v-card-item>
                <v-divider></v-divider>
                <v-card-title> Profitability Aggregate</v-card-title>
                <v-data-table-virtual
                  class="table-bottom table-scroll"
                  :headers="headerFooter"
                  :items="footerItem"
                  :loading="!loading"
                >
                </v-data-table-virtual>
              </v-card-item>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row>
            <v-card-text>
              <v-card-title>Profitability</v-card-title>
              <v-text-field
                v-model="searchQuery"
                prepend-inner-icon="mdi-magnify"
                label="Search"
                variant="outlined"
                single-line
                hide-details
                class="mx-4"
              >
              </v-text-field>
              <v-divider></v-divider>
              <v-data-table-virtual
                class="table-scroll"
                :headers="headers"
                :items="clientsProfitabilityDashboard"
                :search="searchQuery"
                fixed-header=""
                :loading="!loading"
              >
                <template v-slot:item.CSTimeSpentVSAvailableFTECSM="{ value }">
                  <v-chip :color="getColorForPercentageVS(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template v-slot:item.PsTimeSpentVSAvailableFTE="{ value }">
                  <v-chip :color="getColorForPercentageVS(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template
                  v-slot:item.ManDaysUsedVSAvailableTimeIntoContract="{ value }"
                >
                  <v-chip :color="getColorForPercentageManDays(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template v-slot:item.AverageMUVsVSEstimatedMUVs="{ value }">
                  <v-chip :color="getColorForPercentageVS(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template v-slot:item.ECOCSMTime="{ value }">
                  <v-chip :color="getColorForEco(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template v-slot:item.ECOPSTime="{ value }">
                  <v-chip :color="getColorForEco(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template v-slot:item.ECOMUVResourceUsage="{ value }">
                  <v-chip :color="getColorForEco(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template v-slot:item.ECOTotal="{ value }">
                  <v-chip :color="getColorForEco(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template v-slot:item.MonthsIntoTheContract="{ value }">
                  <v-chip :color="getColorForEco(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template v-slot:item.TotalAnnualisedCostOverrun="{ value }">
                  <v-chip :color="getColorForEco(value)">
                    {{ value }}
                  </v-chip>
                </template>
                <template #bottom></template>
              </v-data-table-virtual>
            </v-card-text>
          </v-row>
        </v-col>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, getCurrentInstance, computed } from "vue";
import { useClientStore } from "../stores/client";
import { storeToRefs } from "pinia";
import _ from "lodash";

const clientStore = useClientStore();

const loading = ref(false);
const filterList = ref([]);
let originalClientsProfitabilityDashboard = [];
const clientsProfitabilityDashboardSorted = ref([]);
const footerItem = ref([]);
const instance = getCurrentInstance();
const Name = ref("");

const {
  clientsProfitabilityDashboard,
  clientProfitabilityAggregateTotal,
  clientProfitabilityAggregateAverage,
} = storeToRefs(clientStore);

const {
  fetchClientsProfitabilityDashboard,
  exportProfitalibityDashboard,
  csvExportClientProfitabilityDashboard,
} = clientStore;

const numberWithCommas = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const getColorForPercentageVS = (value) => {
  let valInt = parseInt(value.split(" ")[0]);
  if (valInt >= 100) {
    return "red";
  } else if (valInt > 50) {
    return "orange";
  } else {
    return "green";
  }
};

const getColorForPercentageManDays = (value) => {
  let valInt = parseInt(value.split(" ")[0]);
  if (valInt > 110 || valInt <= 70) {
    return "red";
  } else if ((valInt > 70 && valInt <= 80) || (valInt > 100 && valInt <= 110)) {
    return "orange";
  } else if (valInt > 80 && valInt <= 100) {
    return "green";
  }
};

const getColorForEco = (value) => {
  if (value !== "-") {
    return "blue";
  } else {
    return "none";
  }
};

const headers = [
  { title: "Client Name", key: "Name", fixed: true, width: "33%" },
  { title: "CSM Name", key: "CSMName" },
  { title: "Pod", key: "Pod" },
  { title: "CSM Office", key: "CSMOffice" },
  { title: "KAM Name", key: "KAMName" },
  { title: "Start Date", key: "StartDate" },
  { title: "End Date", key: "EndDate" },
  { title: "Man-days Contracted", key: "ManDaysContracted" },
  { title: "Man-days Contracted Per Month", key: "ManDaysContractedPerMonth" },
  { title: "Contract Term", key: "ContractTerm" },
  { title: "Months Left In Contract", key: "MonthsLeftInContract" },
  { title: "Currency (local)", key: "Currency" },
  {
    title: "License Total Price (Local)",
    key: "LicenseTotalPriceLocal",
    value: (item) => `${numberWithCommas(item.LicenseTotalPriceLocal)}`,
  },
  {
    title: "License Total Price (EUR)",
    key: "LicenseTotalPrice",
    value: (item) => `${numberWithCommas(item.LicenseTotalPrice)}`,
  },
  {
    title: "10% Of License Total Price",
    key: "TenPercentOfLicenseTotalPrice",
    value: (item) => `${numberWithCommas(item.TenPercentOfLicenseTotalPrice)}`,
  },
  {
    title: "10% Of License Total Price Per Month",
    key: "TenPercentOfLicenseTotalPrice",
    value: (item) =>
      `${numberWithCommas(item.TenPercentOfLicenseTotalPricePerMonth)}`,
  },
  {
    title: "CS Plan Price (Local)",
    key: "CSPlanPriceLocal",
    value: (item) => `${numberWithCommas(item.CSPlanPriceLocal)}`,
  },
  {
    title: "CS Plan Price (EUR)",
    key: "CSPlanPrice",
    value: (item) => `${numberWithCommas(item.CSPlanPrice)}`,
  },
  {
    title: "Total CSM Budget",
    key: "TotalCSMBudget",
    value: (item) => `${numberWithCommas(item.TotalCSMBudget)}`,
  },
  {
    title: "Total CSM Budget Per Month",
    key: "TotalCSMBudgetPerMonth",
    value: (item) => `${numberWithCommas(item.TotalCSMBudgetPerMonth)}`,
  },
  {
    title: "Available FTE CSM Per Month (days)",
    key: "AvailableFTECSMPerMonth",
  },
  {
    title: "Available FTE CSM Since Beg Of Contract (days)",
    key: "AvailableFTECSMSinceContractStart",
  },
  { title: "CS 10% Man-days Contracted", key: "CSTenPercentManDaysContracted" },
  {
    title: "CS 10% Man-days Contracted Per Month",
    key: "CSTenPercentManDaysContractedPerMonth",
  },
  {
    title: "CS Available 10% Man-days Contracted Since Beg Of Contract (days)",
    key: "CSTenPercentManDaysContractedSinceContractStart",
  },
  {
    title: "CS Total Time Available Since Beg Of Contract",
    key: "CSTotalTimeAvailableSinceContractStart",
  },
  {
    title: "CS Time Spent Since Beg Of Contract (days)",
    key: "CSTotalTimeSpentSince",
  },
  {
    title: "CS Time Spent VS Available FTE",
    align: "end",
    key: "CSTimeSpentVSAvailableFTECSM",
    value: (item) => `${item.CSTimeSpentVSAvailableFTECSM} %`,
  },
  {
    title: "PS 90% Man-days Contracted",
    key: "PSNinetyPercentManDaysContracted",
  },
  {
    title: "PS 90% Man-days Contracted Per Months (days)",
    key: "PSNinetyPercentManDaysContractedPerMonth",
  },
  {
    title: "PS Available 90% Man-days Contracted Since Beg Of Contract (days)",
    key: "PSNinetyPercentManDaysContractedSinceContractStart",
  },
  {
    title: "PS Time Spent Since Beg Of Contract (days)",
    key: "PSTimeSpentSinceContractStart",
  },
  {
    title: "PS Time Spent VS Available FTE",
    key: "PsTimeSpentVSAvailableFTE",
    value: (item) => `${item.PsTimeSpentVSAvailableFTE} %`,
  },
  { title: "Man-days Used", key: "ManDaysUsed" },
  {
    title: "Man-days Available Since Beg Of Contract",
    key: "ManDaysAvailableSinceContractStart",
  },
  {
    title: "Man-days Used VS Available",
    key: "ManDaysUsedVSAvailableTimeIntoContract",
    value: (item) => {
      if (item.ManDaysContracted >= 1) {
        return `${item.ManDaysUsedVSAvailableTimeIntoContract} %`;
      } else {
        return "-";
      }
    },
  },
  { title: "Man-days Left", key: "ManDaysLeft" },
  { title: "Man-days Left Per Months", key: "ManDaysLeftPerMonth" },
  {
    title: "Contracted MUVs",
    key: "ContractedMUVs",
    value: (item) => `${numberWithCommas(item.ContractedMUVs)}`,
  },
  {
    title: "UVs Since Opport Start Date",
    key: "UVsSinceLastOpportunity",
    value: (item) => `${numberWithCommas(item.UVsSinceLastOpportunity)}`,
  },
  {
    title: "Actual MUVs",
    key: "ActualMUVs",
    value: (item) => `${numberWithCommas(item.ActualMUVs)}`,
  },
  {
    title: "Actual VS Contracted MUVs",
    key: "ActualsVSContractedMUVs",
    value: (item) => `${item.ActualsVSContractedMUVs} %`,
  },
  { title: "% Of UVs Used", key: "PercentageOfUVsUsed" },
  {
    title: "% of Time Into The Contract",
    key: "PercentageIntoContract",
    value: (item) => `${item.PercentageIntoContract} %`,
  },
  {
    title: "% of Time Into The Opportunity",
    key: "PercentageIntoTheOpportunity",
    value: (item) => `${item.PercentageIntoTheOpportunity} %`,
  },
  { title: "SF ID", key: "SFAccountId" },
  {
    title: "Estimated Cost Overrun",
    align: "center",
    children: [
      {
        title: "CS Time",
        key: "ECOCSMTime",
        value: (item) => {
          return item.ECOCSMTime > 0
            ? `${numberWithCommas(item.ECOCSMTime)}`
            : "-";
        },
      },
      {
        title: "PS Time",
        key: "ECOPSTime",
        value: (item) => {
          return item.ECOPSTime > 0
            ? `${numberWithCommas(item.ECOPSTime)}`
            : "-";
        },
      },
      {
        title: "MUV Resource Usage",
        key: "ECOMUVResourceUsage",
        value: (item) => {
          return item.ECOMUVResourceUsage > 0
            ? `${numberWithCommas(item.ECOMUVResourceUsage)}`
            : "-";
        },
      },
      {
        title: "Total",
        key: "ECOTotal",
        value: (item) => {
          return item.ECOTotal > 0 ? `${numberWithCommas(item.ECOTotal)}` : "-";
        },
      },
      { title: "Months Into The Contract", key: "MonthsIntoTheContract" },
      {
        title: "Total Annualised Cost Overrun",
        key: "TotalAnnualisedCostOverrun",
        value: (item) => {
          return item.TotalAnnualisedCostOverrun > 0
            ? `${numberWithCommas(item.TotalAnnualisedCostOverrun)}`
            : "-";
        },
      },
    ],
  },
];

const headerFooter = [
  { title: "Aggregate Type", key: "AggregateType" },
  {
    title: "Man-days Contracted",
    key: "ManDaysContracted",
    value: (item) => `${numberWithCommas(item.ManDaysContracted)}`,
  },
  {
    title: "Man-days Contracted Per Month",
    key: "ManDaysContractedPerMonth",
    value: (item) => `${numberWithCommas(item.ManDaysContractedPerMonth)}`,
  },
  {
    title: "Contract Term",
    key: "ContractTerm",
    value: (item) => `${numberWithCommas(item.ContractTerm)}`,
  },
  {
    title: "Months Left In Contract",
    key: "MonthsLeftInContract",
    value: (item) => `${numberWithCommas(item.MonthsLeftInContract)}`,
  },
  {
    title: "License Total Price (EUR)",
    key: "LicenseTotalPrice",
    value: (item) => `${numberWithCommas(item.LicenseTotalPrice)}`,
  },
  {
    title: "10% Of License Total Price",
    key: "TenPercentOfLicenseTotalPrice",
    value: (item) => `${numberWithCommas(item.TenPercentOfLicenseTotalPrice)}`,
  },
  {
    title: "10% Of License Total Price Per Month",
    key: "TenPercentOfLicenseTotalPrice",
    value: (item) =>
      `${numberWithCommas(item.TenPercentOfLicenseTotalPricePerMonth)}`,
  },
  {
    title: "CS Plan Price (EUR)",
    key: "CSPlanPrice",
    value: (item) => `${numberWithCommas(item.CSPlanPrice)}`,
  },
  {
    title: "Total CSM Budget",
    key: "TotalCSMBudget",
    value: (item) => `${numberWithCommas(item.TotalCSMBudget)}`,
  },
  {
    title: "Total CSM Budget Per Month",
    key: "TotalCSMBudgetPerMonth",
    value: (item) => `${numberWithCommas(item.TotalCSMBudgetPerMonth)}`,
  },
  {
    title: "Available FTE CSM Per Month (days)",
    key: "AvailableFTECSMPerMonth",
    value: (item) => `${numberWithCommas(item.AvailableFTECSMPerMonth)}`,
  },
  {
    title: "Available FTE CSM Since Beg Of Contract (days)",
    key: "AvailableFTECSMSinceContractStart",
    value: (item) =>
      `${numberWithCommas(item.AvailableFTECSMSinceContractStart)}`,
  },
  {
    title: "CS 10% Man-days Contracted",
    key: "CSTenPercentManDaysContracted",
    value: (item) => `${numberWithCommas(item.CSTenPercentManDaysContracted)}`,
  },
  {
    title: "CS 10% Man-days Contracted Per Month",
    key: "CSTenPercentManDaysContractedPerMonth",
    value: (item) =>
      `${numberWithCommas(item.CSTenPercentManDaysContractedPerMonth)}`,
  },
  {
    title: "CS Available 10% Man-days Contracted Since Beg Of Contract (days)",
    key: "CSTenPercentManDaysContractedSinceContractStart",
    value: (item) =>
      `${numberWithCommas(
        item.CSTenPercentManDaysContractedSinceContractStart
      )}`,
  },
  {
    title: "CS Total Time Available Since Beg Of Contract",
    key: "CSTotalTimeAvailableSinceContractStart",
    value: (item) =>
      `${numberWithCommas(item.CSTotalTimeAvailableSinceContractStart)}`,
  },
  {
    title: "CS Time Spent Since Beg Of Contract (days)",
    key: "CSTotalTimeSpentSince",
    value: (item) => `${numberWithCommas(item.CSTotalTimeSpentSince)}`,
  },
  {
    title: "CS Time Spent VS Available FTE",
    align: "end",
    key: "CSTimeSpentVSAvailableFTECSM",
    value: (item) => `${item.CSTimeSpentVSAvailableFTECSM} %`,
  },
  {
    title: "CS Time Spent VS Available FTE",
    align: "end",
    key: "CSTimeSpentVSAvailableFTECSM",
    value: (item) => `${item.CSTimeSpentVSAvailableFTECSM} %`,
  },
  {
    title: "PS 90% Man-days Contracted Per Months (days)",
    key: "PSNinetyPercentManDaysContractedPerMonth",
    value: (item) =>
      `${numberWithCommas(item.PSNinetyPercentManDaysContractedPerMonth)}`,
  },
  {
    title: "PS Available 90% Man-days Contracted Since Beg Of Contract (days)",
    key: "PSNinetyPercentManDaysContractedSinceContractStart",
    value: (item) =>
      `${numberWithCommas(
        item.PSNinetyPercentManDaysContractedSinceContractStart
      )}`,
  },
  {
    title: "PS Time Spent Since Beg Of Contract (days)",
    key: "PSTimeSpentSinceContractStart",
    value: (item) => `${numberWithCommas(item.PSTimeSpentSinceContractStart)}`,
  },
  {
    title: "PS Time Spent VS Available FTE",
    key: "PsTimeSpentVSAvailableFTE",
    value: (item) => `${item.PsTimeSpentVSAvailableFTE} %`,
  },
  {
    title: "Man-days Used",
    key: "ManDaysUsed",
    value: (item) => `${numberWithCommas(item.ManDaysUsed)}`,
  },
  {
    title: "Man-days Available Since Beg Of Contract",
    key: "ManDaysAvailableSinceContractStart",
    value: (item) =>
      `${numberWithCommas(item.ManDaysAvailableSinceContractStart)}`,
  },
  {
    title: "Man-days Used VS Available",
    key: "ManDaysUsedVSAvailableTimeIntoContract",
    value: (item) => `${item.ManDaysUsedVSAvailableTimeIntoContract} %`,
  },
  {
    title: "Man-days Left",
    key: "ManDaysLeft",
    value: (item) => `${numberWithCommas(item.ManDaysLeft)}`,
  },
  {
    title: "Man-days Left Per Months",
    key: "ManDaysLeftPerMonth",
    value: (item) => `${numberWithCommas(item.ManDaysLeftPerMonth)}`,
  },
  {
    title: "Contracted MUVs",
    key: "ContractedMUVs",
    value: (item) => `${numberWithCommas(item.ContractedMUVs)}`,
  },
  {
    title: "UVs Since Opport Start Date",
    key: "UVsSinceLastOpportunity",
    value: (item) => `${numberWithCommas(item.UVsSinceLastOpportunity)}`,
  },
  {
    title: "Actual MUVs",
    key: "ActualMUVs",
    value: (item) => `${numberWithCommas(item.ActualMUVs)}`,
  },
  {
    title: "Actual VS Contracted MUVs",
    key: "ActualsVSContractedMUVs",
    value: (item) => `${item.ActualsVSContractedMUVs} %`,
  },
  {
    title: "% Of UVs Used",
    key: "PercentageOfUVsUsed",
    value: (item) => `${numberWithCommas(item.PercentageOfUVsUsed)} %`,
  },
  {
    title: "% of Time Into The Contract",
    key: "PercentageIntoContract",
    value: (item) => `${item.PercentageIntoContract} %`,
  },
  {
    title: "% of Time Into The Opportunity",
    key: "PercentageIntoTheOpportunity",
    value: (item) => `${item.PercentageIntoTheOpportunity} %`,
  },
  {
    title: "CS Time",
    key: "ECOCSMTime",
    value: (item) => {
      return item.ECOCSMTime > 0 ? `${numberWithCommas(item.ECOCSMTime)}` : "-";
    },
  },
  {
    title: "PS Time",
    key: "ECOPSTime",
    value: (item) => {
      return item.ECOPSTime > 0 ? `${numberWithCommas(item.ECOPSTime)}` : "-";
    },
  },
  {
    title: "MUV Resource Usage",
    key: "ECOMUVResourceUsage",
    value: (item) => {
      return item.ECOMUVResourceUsage > 0
        ? `${numberWithCommas(item.ECOMUVResourceUsage)}`
        : "-";
    },
  },
  {
    title: "Total",
    key: "ECOTotal",
    value: (item) => {
      return item.ECOTotal > 0 ? `${numberWithCommas(item.ECOTotal)}` : "-";
    },
  },
  {
    title: "Months Into The Contract",
    key: "MonthsIntoTheContract",
    value: (item) => `${numberWithCommas(item.MonthsIntoTheContract)}`,
  },
  {
    title: "Total Annualised Cost Overrun",
    key: "TotalAnnualisedCostOverrun",
    value: (item) => {
      return item.TotalAnnualisedCostOverrun > 0
        ? `${numberWithCommas(item.TotalAnnualisedCostOverrun)}`
        : "-";
    },
  },
];

const searchQuery = ref("");
let timeout;

watch(searchQuery, (newQuery) => {
  clearTimeout(timeout);
  timeout = setTimeout(() => {}, 500);
});

onMounted(async () => {
  await fetchClientsProfitabilityDashboard();
  originalClientsProfitabilityDashboard = [
    ...clientsProfitabilityDashboard.value,
  ];
  clientsProfitabilityDashboardSorted.value = [
    ...originalClientsProfitabilityDashboard,
  ];
  footerItem.value.push(clientProfitabilityAggregateTotal.value);
  footerItem.value.push(clientProfitabilityAggregateAverage.value);
  loading.value = true;
  // emit('loading', true);
});
</script>

<style lang="scss">
.export-div {
  display: flex;
  justify-content: flex-start;
  margin-left: 2.5vw;
  margin-top: 1.5vh;
}

.grid-button-wrapper {
  width: min-content;
  height: min-content;
  justify-content: end;
  display: grid;
  grid-template-columns: repeat(1, min-content);
  grid-template-rows: repeat(2, auto);
}

.btn-sort-size {
  width: min-content !important;
  height: min-content !important;
}

.table-wrapper {
  @media (min-width: 50px) {
    display: flex;
    flex-direction: row;
    width: max-content;
    height: max-content;
  }
  @media (min-width: 1024px) {
    width: max-content;
    height: max-content;
    margin-bottom: auto;
    margin-left: 1.5vw;
    margin-right: 1.5vw;
    display: flex;
    flex-direction: row;
  }
}

.table-scroll {
  @media (min-width: 50px) {
    width: max-content;
    height: max-content;
    overflow-x: scroll;
  }
  @media (min-width: 1024px) {
    width: 97vw !important;
    height: 120vh !important;
    overflow-x: scroll;
  }
}
.table-bottom {
  padding-top: 1vh !important;
}
</style>